/**
 * AI 판정 카드 — 결과 페이지 최상단 hero.
 * line_item 검색 시에만 표시.
 */
import React, { useState, useEffect } from "react";
import { Download, Search, ChevronDown, ChevronUp } from "lucide-react";
import EvidenceCards from "./EvidenceCards";
import { downloadAuditPdf } from "./api";
import "./theme.css";

// 사용자 정정 학습 loop — feedback chip
function FeedbackChip({ response, line_item }) {
  const [state, setState] = useState("idle"); // idle | editing | submitted
  const [corrected, setCorrected] = useState("");
  const [err, setErr] = useState(null);
  const predicted = response?.hs_10digit || response?.hs_6digit || "";

  const submit = async (final) => {
    setErr(null);
    try {
      const r = await fetch("/api/onto/feedback", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          audit_id: response?.audit_id || null,
          line_item,
          predicted_hsk: predicted,
          corrected_hsk: final,
        }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setState("submitted");
    } catch (e) {
      setErr(String(e.message || e));
    }
  };

  if (state === "submitted") {
    return (
      <div
        style={{
          marginTop: 8,
          padding: 6,
          background: "#e8f5e9",
          color: "#1b5e20",
          borderRadius: 4,
          fontSize: 12,
        }}
      >
        ✓ 피드백 반영됨. ontology 가 누적되어 자동 학습됩니다.
      </div>
    );
  }
  if (state === "editing") {
    return (
      <div
        style={{
          marginTop: 8,
          padding: 8,
          background: "#fff8e1",
          border: "1px solid #f9a825",
          borderRadius: 4,
          fontSize: 12,
        }}
      >
        <div style={{ marginBottom: 4 }}>정정할 HSK (10자리):</div>
        <div style={{ display: "flex", gap: 4 }}>
          <input
            value={corrected}
            onChange={(e) =>
              setCorrected(e.target.value.replace(/[^0-9]/g, ""))
            }
            maxLength={10}
            placeholder="예: 6109101000"
            style={{
              flex: 1,
              padding: "4px 8px",
              fontSize: 12,
              border: "1px solid var(--audit-border)",
              borderRadius: 3,
              fontFamily: "JetBrains Mono, monospace",
            }}
          />
          <button
            onClick={() => submit(corrected)}
            disabled={corrected.length < 6}
            style={{
              padding: "4px 10px",
              fontSize: 12,
              background: "#f9a825",
              color: "white",
              border: "none",
              borderRadius: 3,
              cursor: "pointer",
            }}
          >
            정정
          </button>
          <button
            onClick={() => setState("idle")}
            style={{
              padding: "4px 10px",
              fontSize: 12,
              border: "1px solid var(--audit-border)",
              background: "transparent",
              borderRadius: 3,
              cursor: "pointer",
            }}
          >
            취소
          </button>
        </div>
        {err && <div style={{ marginTop: 4, color: "#d93025" }}>{err}</div>}
      </div>
    );
  }
  return (
    <div
      style={{
        marginTop: 8,
        display: "flex",
        gap: 6,
        fontSize: 12,
        alignItems: "center",
      }}
    >
      <span style={{ color: "var(--audit-text-muted)" }}>
        이 분류가 맞나요?
      </span>
      <button
        onClick={() => submit(predicted)}
        disabled={!predicted}
        style={{
          padding: "3px 10px",
          fontSize: 12,
          background: "#e8f5e9",
          color: "#1b5e20",
          border: "1px solid #2e7d32",
          borderRadius: 12,
          cursor: "pointer",
        }}
      >
        ✓ 맞음
      </button>
      <button
        onClick={() => setState("editing")}
        style={{
          padding: "3px 10px",
          fontSize: 12,
          background: "#fff3e0",
          color: "#e65100",
          border: "1px solid #ef6c00",
          borderRadius: 12,
          cursor: "pointer",
        }}
      >
        정정
      </button>
    </div>
  );
}

// 진짜 ontology: HS class 별 권장 attribute auto-fetch
function OntologyRulesChip({ hs }) {
  const [rules, setRules] = useState([]);
  useEffect(() => {
    if (!hs) return setRules([]);
    fetch(`/api/onto/hs-attributes?hs=${hs}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => setRules(d?.rules || []))
      .catch(() => setRules([]));
  }, [hs]);
  if (rules.length === 0) return null;
  return (
    <div
      style={{
        padding: 10,
        marginTop: 8,
        background: "var(--audit-bg-subtle)",
        color: "var(--audit-text)",
        border: "1px solid var(--audit-border)",
        borderRadius: 6,
        fontSize: 12,
      }}
    >
      <div
        style={{
          fontWeight: 700,
          marginBottom: 6,
          color: "var(--audit-text)",
        }}
      >
        Ontology 권장 attribute (HS {hs})
      </div>
      {rules.slice(0, 6).map((r, i) => (
        <div key={i} style={{ marginBottom: 3, lineHeight: 1.5 }}>
          <span
            style={{
              fontFamily: "JetBrains Mono, monospace",
              color: "var(--audit-text-muted)",
            }}
          >
            [{r.hs_prefix}]
          </span>{" "}
          <b>{r.attribute_key}</b>
          {r.is_required && (
            <span style={{ color: "var(--audit-text-muted)", fontWeight: 700 }}>
              {" "}
              *필수
            </span>
          )}
          <span style={{ color: "var(--audit-text-muted)" }}>: </span>
          <span>{(r.valid_values || []).join(" / ")}</span>
        </div>
      ))}
    </div>
  );
}

function ConfidenceBar({ level }) {
  const filled = level === "high" ? 5 : level === "medium" ? 3 : 1;
  return (
    <span style={{ display: "inline-flex", gap: 2, alignItems: "center" }}>
      {[1, 2, 3, 4, 5].map((i) => (
        <span
          key={i}
          style={{
            width: 12,
            height: 6,
            background:
              i <= filled
                ? level === "medium"
                  ? "var(--audit-conf-medium)"
                  : "var(--audit-conf-high)"
                : "var(--audit-border)",
            borderRadius: 1,
          }}
        />
      ))}
      <span
        style={{
          marginLeft: 8,
          fontSize: 12,
          color: "var(--audit-text-muted)",
          textTransform: "uppercase",
        }}
      >
        {level || "—"}
      </span>
    </span>
  );
}

export default function AIVerdictCard({
  response,
  line_item,
  loading = false,
  onOpenPrecedents,
  onHsClick,
}) {
  const [expanded, setExpanded] = useState(false);

  if (loading) {
    return (
      <div className="audit-decision" style={{ marginBottom: 16 }}>
        <div style={{ color: "var(--audit-text-muted)", fontSize: 13 }}>
          AI 분류 진행 중... (Dual-Evidence pipeline)
        </div>
      </div>
    );
  }

  if (!response) return null;

  const ev = response.audit_evidence;
  const isAudit = response.routing?.path === "audit";
  const cases = ev?.precedent_cases || [];

  return (
    <div className="audit-decision" style={{ marginBottom: 16 }}>
      <div style={{ display: "flex", alignItems: "start", gap: 16 }}>
        <div style={{ flex: 1 }}>
          <div
            className="audit-decision-hs10"
            onClick={() => {
              const hs = response.hs_10digit || response.hs_6digit;
              if (onHsClick && hs) onHsClick(hs);
            }}
            title={onHsClick ? "클릭 — 이 HSK 기준으로 5-section 필터" : ""}
            style={onHsClick ? { cursor: "pointer" } : {}}
          >
            {response.hs_10digit || response.hs_6digit || "(미확정)"}
          </div>
          <div className="audit-decision-desc">
            {response.description_ko || "(품명 미확정)"}
          </div>
          <div
            style={{
              marginTop: 10,
              display: "flex",
              alignItems: "center",
              gap: 12,
              fontSize: 12,
              color: "var(--audit-text-muted)",
            }}
          >
            <ConfidenceBar level={response.confidence || "low"} />
            <span
              style={{
                padding: "3px 8px",
                borderRadius: 4,
                border: "1px solid var(--audit-border)",
                fontFamily: "JetBrains Mono, monospace",
                fontSize: 12,
              }}
            >
              {isAudit ? "Audit" : "Fast"} · {response.chapter}류
            </span>
            {ev?.applied_gri?.length > 0 && (
              <span style={{ fontSize: 12 }}>
                {ev.applied_gri.join(" + ")} · 선례 {cases.length}건{" "}
                {ev.precedent_agreement || ""}
              </span>
            )}
          </div>
          {ev?.requires_human_review && (
            <div
              style={{
                marginTop: 10,
                padding: "6px 10px",
                borderLeft: "2px solid var(--audit-accent)",
                background: "var(--audit-accent-bg)",
                fontSize: 12,
                color: "var(--audit-text)",
              }}
            >
              ⓘ 검토 필요 — {ev.review_reason || "관세사 추가 검토 권장"}
            </div>
          )}
          {/* 진짜 ontology: 권장 attribute 자동 노출 */}
          <OntologyRulesChip
            hs={response.hs_10digit || response.hs_6digit || response.chapter}
          />
          {/* 사용자 정정 학습 loop */}
          <FeedbackChip response={response} line_item={line_item} />
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {isAudit && (
            <button
              className="audit-btn audit-btn-primary"
              onClick={async () => {
                try {
                  await downloadAuditPdf({
                    line_item,
                    chapter: response.chapter,
                  });
                } catch (e) {
                  alert("PDF 다운로드 실패: " + e);
                }
              }}
              style={{ display: "flex", alignItems: "center", gap: 4 }}
            >
              <Download size={14} /> 컨펌 PDF
            </button>
          )}
          {onOpenPrecedents && (
            <button
              className="audit-btn"
              onClick={onOpenPrecedents}
              style={{ display: "flex", alignItems: "center", gap: 4 }}
            >
              <Search size={14} /> 전체 선례
            </button>
          )}
          <button
            className="audit-btn"
            onClick={() => setExpanded((e) => !e)}
            style={{ display: "flex", alignItems: "center", gap: 4 }}
          >
            {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            상세 evidence
          </button>
        </div>
      </div>
      {expanded && (
        <div
          style={{
            marginTop: 14,
            paddingTop: 14,
            borderTop: "1px solid var(--audit-border)",
          }}
        >
          <EvidenceCards response={response} />
        </div>
      )}
    </div>
  );
}
